import debounce from 'lodash/debounce'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { mainColors } from '@avantstay/backoffice-core'
import { IconSearch } from '@avantstay/backoffice-vectors'

import useKeyBinding from '@hooks/useKeyBinding'

import { SearchInputProps } from './__types'
import * as S from './SearchInput.styles'

const DEBOUNCE_TIME = 300

function _SearchInput({
  onChange,
  onFocus,
  placeholder,
  isValidKeyBind,
  iconSize = '24px',
  baseColor = mainColors.white,
  iconColor = mainColors.neutralExtremelyDark,
  backgroundColor = 'transparent',
  fontSize = '16px',
  spaceAfterIcon = '8px',
  placeholderColor,
  defaultValue = '',
  className,
  autoFocus = false,
  dataCy,
}: SearchInputProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null)
  const [value, setValue] = useState(defaultValue)
  const useKeyBindCallback = useCallback(
    (e: KeyboardEvent) => {
      if (isValidKeyBind && isValidKeyBind(e)) {
        e?.preventDefault()
        inputRef.current?.focus?.()
      }
    },
    [isValidKeyBind],
  )

  useKeyBinding(useKeyBindCallback)

  const focusInput = () => inputRef.current?.focus?.()
  const debouncedOnChange = useMemo(() => debounce(onChange, DEBOUNCE_TIME), [onChange])

  useEffect(() => {
    if (value !== '') {
      // Refocus the input when the value is changed due onChange debounce.
      focusInput()
    }
  }, [value])

  const handleReset = () => {
    setValue('')
    onChange('')
  }

  return (
    <S.Root className={className}>
      <IconSearch color={iconColor || baseColor} width={iconSize} onClick={focusInput} />
      <S.Input
        baseColor={baseColor}
        backgroundColor={backgroundColor}
        placeholder={placeholder}
        fontSize={fontSize}
        spaceAfterIcon={spaceAfterIcon}
        placeholderColor={placeholderColor}
        onChange={e => {
          const newValue = e?.target.value
          setValue(newValue)
          e?.persist()
          debouncedOnChange(newValue)
        }}
        onFocus={onFocus}
        ref={inputRef}
        onClick={e => e?.stopPropagation()}
        autoFocus={autoFocus || defaultValue !== ''}
        value={value}
        data-cy={dataCy}
      />
      {value.length > 0 ? (
        <S.ClearButton onClick={handleReset}>
          <S.ClearButtonIcon fill={baseColor} />
        </S.ClearButton>
      ) : undefined}
    </S.Root>
  )
}

/**
 * @deprecated use "@avantstay/backoffice-core" instead
 */
export const SearchInput = _SearchInput
